import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import {
  TriangulationResult,
  PredictiveEventType,
  FinalAssessmentStatus,
  EvidenceStatus,
  V519PresentationMode
} from '../../types/v519';
import { DEFAULT_TRIANGULATION_WEIGHTS } from '../../utils/v519/triangulationEngine';
import { triangulateCanonicalEvent as triangulateEvent } from '../../utils/dashaGocharSynthesisEngine';
import { EVENT_RULE_REGISTRY } from '../../utils/v519/eventPredictionEngine';
import {
  Layers,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  Shield,
  ArrowRight,
  Sparkles,
  BookOpen,
  Filter,
  BarChart3,
  Scale,
  Compass,
  Zap,
  Info
} from 'lucide-react';

export interface TriangulationMatrixCardProps {
  kundali: CompleteKundali;
  triangulationResult?: TriangulationResult;
  selectedEventType?: PredictiveEventType;
  onSelectEventType?: (eventType: PredictiveEventType) => void;
  lang: Language;
  presentationMode?: V519PresentationMode;
  onOpenEvidenceDrawer?: (eventType: PredictiveEventType, ruleId?: string) => void;
}

const ASSESSMENT_BADGES: Record<FinalAssessmentStatus, {
  bg: string;
  border: string;
  text: string;
  labelEn: string;
  labelHi: string;
}> = {
  CONFIRMED: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-800',
    labelEn: 'Triple-Stream Full Consensus (Strong Convergence)',
    labelHi: 'त्रि-धारा पूर्ण शास्त्रीय सहमति (प्रबल अभिसरण)'
  },
  STRONGLY_SUPPORTED: {
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    text: 'text-teal-800',
    labelEn: 'Strong Corroboration (High Alignment)',
    labelHi: 'दृढ़ शास्त्रीय समर्थन (उच्च संरेखन)'
  },
  SUPPORTED: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    labelEn: 'Supported with Minor Nuance (Moderate Convergence)',
    labelHi: 'मध्यम शास्त्रीय समर्थन (साधारण अभिसरण)'
  },
  MIXED: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    labelEn: 'Mixed Signals / Divergent Stream Detected',
    labelHi: 'मिश्रित संकेत / शास्त्रीय मतभेद'
  },
  WEAK: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-800',
    labelEn: 'Weak Corroboration Across Streams',
    labelHi: 'कमजोर शास्त्रीय पुष्टि'
  },
  CONTRADICTED: {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    text: 'text-rose-800',
    labelEn: 'Contradiction Detected Across Streams',
    labelHi: 'शास्त्रीय धाराओं में विरोधाभास'
  },
  INSUFFICIENT_DATA: {
    bg: 'bg-astro-secondary-surface',
    border: 'border-astro-border',
    text: 'text-astro-secondary-text',
    labelEn: 'Data Incomplete / Unverified Coordinates',
    labelHi: 'अपूर्ण गणना / असत्यापित विवरण'
  }
};

const STATUS_PILLS: Record<EvidenceStatus, {
  bg: string;
  text: string;
  labelEn: string;
  labelHi: string;
}> = {
  CONFIRMED: {
    bg: 'bg-emerald-600 text-white',
    text: 'text-emerald-700',
    labelEn: 'Confirmed (Active)',
    labelHi: 'सत्यापित (सक्रिय)'
  },
  SUPPORTING: {
    bg: 'bg-teal-600 text-white',
    text: 'text-teal-700',
    labelEn: 'Supporting',
    labelHi: 'समर्थक'
  },
  NEUTRAL: {
    bg: 'bg-astro-secondary text-white',
    text: 'text-astro-secondary-text',
    labelEn: 'Neutral / Dormant',
    labelHi: 'तटस्थ / सुप्त'
  },
  CONFLICTING: {
    bg: 'bg-rose-600 text-white',
    text: 'text-rose-700',
    labelEn: 'Conflicting',
    labelHi: 'विरोधी'
  },
  UNAVAILABLE: {
    bg: 'bg-amber-600 text-white',
    text: 'text-amber-800',
    labelEn: 'Unavailable / Pending',
    labelHi: 'अनुपलब्ध'
  }
};

export const TriangulationMatrixCard: React.FC<TriangulationMatrixCardProps> = ({
  kundali,
  triangulationResult,
  selectedEventType = 'CAREER_PROMOTION',
  onSelectEventType,
  lang,
  presentationMode = 'PRO',
  onOpenEvidenceDrawer
}) => {
  const isHi = lang === 'hi';
  const isBasic = presentationMode === 'BASIC';
  const isLearner = (presentationMode as string) === 'LEARNER';
  const isPro = presentationMode === 'PRO';

  const [activeType, setActiveType] = useState<PredictiveEventType>(selectedEventType);

  const handleSelectType = (type: PredictiveEventType) => {
    setActiveType(type);
    if (onSelectEventType) {
      onSelectEventType(type);
    }
  };

  // Compute triangulation deterministically if not provided
  const currentTriangulation = useMemo(() => {
    if (triangulationResult && triangulationResult.eventType === activeType) {
      return triangulationResult;
    }
    return triangulateEvent(kundali, activeType, new Date());
  }, [kundali, triangulationResult, activeType]);

  const assessmentMeta = ASSESSMENT_BADGES[currentTriangulation.finalAssessment] || ASSESSMENT_BADGES.SUPPORTED;
  const pStatus = STATUS_PILLS[currentTriangulation.parashariEvidence.status] || STATUS_PILLS.NEUTRAL;
  const kStatus = STATUS_PILLS[currentTriangulation.kpEvidence.status] || STATUS_PILLS.NEUTRAL;
  const jStatus = STATUS_PILLS[currentTriangulation.jaiminiEvidence.status] || STATUS_PILLS.NEUTRAL;

  const eventRule = EVENT_RULE_REGISTRY[activeType] || EVENT_RULE_REGISTRY['CAREER_PROMOTION'];
  const allEvents = Object.values(EVENT_RULE_REGISTRY);

  return (
    <section
      id="triangulation-matrix-card"
      aria-labelledby="triangulation-matrix-heading"
      className="bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-6 shadow-xs space-y-6 text-astro-text"
    >
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-astro-border pb-4">
        <div className="flex items-start sm:items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-astro-primary text-astro-accent flex items-center justify-center shrink-0 shadow-xs">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 id="triangulation-matrix-heading" className="text-base sm:text-lg font-bold text-astro-primary font-serif">
                {isHi ? 'त्रि-धारा शास्त्रीय त्रिकोणीयकरण आव्यूह' : 'Three-Stream Methodological Triangulation Matrix'}
              </h3>
              <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-astro-primary/10 text-astro-primary border border-astro-primary/20">
                Parashari • KP • Jaimini
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text mt-0.5">
              {isHi
                ? 'पराशरी विंशोत्तरी (४५%), कृष्णमूर्ति उप-स्वामी (३५%) एवं जैमिनी चर दशा (२०%) की तुलनात्मक पुष्टि'
                : 'Weighted algorithmic consensus across Parashari Vimshottari (45%), KP Sub-Lord (35%) & Jaimini Chara Dasha (20%)'}
            </p>
          </div>
        </div>

        {/* View Mode Pill */}
        <div className="flex items-center gap-2 self-start md:self-center">
          <span className="text-[11px] font-medium text-astro-secondary-text">
            {isHi ? 'प्रस्तुति:' : 'Perspective:'}
          </span>
          <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-astro-card border border-astro-border text-astro-primary">
            {isBasic ? (isHi ? 'बेसिक' : 'Basic') : isLearner ? (isHi ? 'लर्नर' : 'Learner') : (isHi ? 'प्रो' : 'Pro')}
          </span>
        </div>
      </div>

      {/* Event Selector Dropdown / Pills */}
      <div className="space-y-2">
        <label className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-astro-accent" />
          <span>{isHi ? 'सत्यापन हेतु घटना का चयन करें' : 'Select Target Event to Triangulate'}</span>
        </label>
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {allEvents.map((evt) => {
            const isSelected = evt.eventType === activeType;
            return (
              <button
                key={evt.eventType}
                onClick={() => handleSelectType(evt.eventType)}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium border whitespace-nowrap transition-all touch-manipulation cursor-pointer ${
                  isSelected
                    ? 'bg-astro-primary text-white border-astro-primary shadow-xs font-bold'
                    : 'bg-astro-card text-astro-text border-astro-border hover:border-astro-accent'
                }`}
              >
                {isHi ? evt.titleHi : evt.titleEn}
              </button>
            );
          })}
        </div>
      </div>

      {/* Synthesis Score & Consensus Banner */}
      <div className={`p-4 rounded-xl border ${assessmentMeta.border} ${assessmentMeta.bg} space-y-3`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase font-bold text-astro-secondary-text tracking-wider">
              {isHi ? 'शास्त्रीय सहमति निष्कर्ष' : 'Triangulation Synthesis Verdict'}
            </div>
            <div className="text-sm sm:text-base font-bold text-astro-primary font-serif mt-0.5">
              {isHi ? assessmentMeta.labelHi : assessmentMeta.labelEn}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-[11px] text-astro-secondary-text">{isHi ? 'अभिसरण स्कोर' : 'Convergence Score'}</div>
              <div className="text-xs font-bold text-astro-primary">
                {isHi ? `विश्वास स्तर: ${currentTriangulation.confidenceLevel}` : `Confidence: ${currentTriangulation.confidenceLevel}`}
              </div>
            </div>
            <div className="px-3 py-1.5 rounded-xl text-base font-extrabold bg-astro-primary text-white shadow-xs">
              {currentTriangulation.convergenceScore}%
            </div>
          </div>
        </div>

        {/* Basic Consensus Summary Text */}
        {isBasic && (
          <div className="p-3 bg-white/90 rounded-lg border border-astro-border text-xs text-astro-text leading-relaxed">
            {isHi
              ? `तीन प्रमुख वैदिक पद्धतियों में से ${
                  [currentTriangulation.parashariEvidence.status, currentTriangulation.kpEvidence.status, currentTriangulation.jaiminiEvidence.status].filter(s => s === 'CONFIRMED' || s === 'SUPPORTING').length
                } पद्धतियां इस घटना के होने का प्रत्यक्ष समर्थन कर रही हैं। समग्र सामंजस्य स्कोर ${currentTriangulation.convergenceScore}% है।`
              : `${
                  [currentTriangulation.parashariEvidence.status, currentTriangulation.kpEvidence.status, currentTriangulation.jaiminiEvidence.status].filter(s => s === 'CONFIRMED' || s === 'SUPPORTING').length
                } out of 3 major classical streams explicitly corroborate this life event, resulting in a synthesized convergence score of ${currentTriangulation.convergenceScore}%.`}
          </div>
        )}
      </div>

      {/* 3-Column Stream Matrix */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* STREAM 1: PARASHARI (45%) */}
        <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3 flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex items-center justify-between border-b border-astro-border pb-2">
              <div>
                <span className="text-[10px] uppercase font-bold text-astro-accent tracking-wider">
                  {isHi ? 'धारा १ (भार ४५%)' : 'Stream 1 (Weight 45%)'}
                </span>
                <h4 className="text-sm font-bold text-astro-primary">
                  {isHi ? 'पराशरी पद्धति' : 'Parashari System'}
                </h4>
              </div>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${pStatus.bg}`}>
                {isHi ? pStatus.labelHi : pStatus.labelEn}
              </span>
            </div>

            <div className="space-y-1.5 text-xs">
              <div className="flex items-center justify-between text-astro-secondary-text">
                <span>{isHi ? 'दशा स्वामी:' : 'Dasha Lords:'}</span>
                <strong className="text-astro-text">
                  {currentTriangulation.parashariEvidence.mahadashaLord}–{currentTriangulation.parashariEvidence.antardashaLord}
                </strong>
              </div>

              <div className="flex items-center justify-between text-astro-secondary-text">
                <span>{isHi ? 'सक्रिय भाव:' : 'Active Houses:'}</span>
                <span className="font-mono font-bold text-astro-primary">
                  {currentTriangulation.parashariEvidence.activeHouses.join(', ')}
                </span>
              </div>

              <p className="text-astro-secondary-text text-[11px] leading-relaxed pt-1">
                {isHi ? currentTriangulation.parashariEvidence.explanationHi : currentTriangulation.parashariEvidence.explanationEn}
              </p>
            </div>
          </div>

          <div className="pt-2 border-t border-astro-border flex items-center justify-between text-xs">
            <span className="text-astro-secondary-text">{isHi ? 'धारा स्कोर:' : 'Stream Score:'}</span>
            <span className="font-extrabold text-astro-primary">{currentTriangulation.parashariEvidence.score}/100</span>
          </div>
        </div>

        {/* STREAM 2: KP SYSTEM (35%) */}
        <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3 flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex items-center justify-between border-b border-astro-border pb-2">
              <div>
                <span className="text-[10px] uppercase font-bold text-astro-accent tracking-wider">
                  {isHi ? 'धारा २ (भार ३५%)' : 'Stream 2 (Weight 35%)'}
                </span>
                <h4 className="text-sm font-bold text-astro-primary">
                  {isHi ? 'के.पी. ज्योतिष (Sub-Lord)' : 'KP System (Sub-Lord)'}
                </h4>
              </div>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${kStatus.bg}`}>
                {isHi ? kStatus.labelHi : kStatus.labelEn}
              </span>
            </div>

            <div className="space-y-1.5 text-xs">
              <div className="flex items-center justify-between text-astro-secondary-text">
                <span>{isHi ? 'प्राथमिक भाव / उप-स्वामी:' : 'Primary Cusp / Sub-Lord:'}</span>
                <strong className="text-astro-text">
                  Cusp {currentTriangulation.kpEvidence.primaryCusp} ({currentTriangulation.kpEvidence.subLordPlanet})
                </strong>
              </div>

              <div className="flex items-center justify-between text-astro-secondary-text">
                <span>{isHi ? 'नक्षत्र स्वामी:' : 'Star Lord:'}</span>
                <span className="font-bold text-astro-primary">{currentTriangulation.kpEvidence.starLordPlanet}</span>
              </div>

              <p className="text-astro-secondary-text text-[11px] leading-relaxed pt-1">
                {isHi ? currentTriangulation.kpEvidence.explanationHi : currentTriangulation.kpEvidence.explanationEn}
              </p>
            </div>
          </div>

          <div className="pt-2 border-t border-astro-border flex items-center justify-between text-xs">
            <span className="text-astro-secondary-text">{isHi ? 'धारा स्कोर:' : 'Stream Score:'}</span>
            <span className="font-extrabold text-astro-primary">{currentTriangulation.kpEvidence.score}/100</span>
          </div>
        </div>

        {/* STREAM 3: JAIMINI SUTRAS (20%) */}
        <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3 flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex items-center justify-between border-b border-astro-border pb-2">
              <div>
                <span className="text-[10px] uppercase font-bold text-astro-accent tracking-wider">
                  {isHi ? 'धारा ३ (भार २०%)' : 'Stream 3 (Weight 20%)'}
                </span>
                <h4 className="text-sm font-bold text-astro-primary">
                  {isHi ? 'जैमिनी चर दशा व कारक' : 'Jaimini Chara Dasha'}
                </h4>
              </div>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${jStatus.bg}`}>
                {isHi ? jStatus.labelHi : jStatus.labelEn}
              </span>
            </div>

            <div className="space-y-1.5 text-xs">
              <div className="flex items-center justify-between text-astro-secondary-text">
                <span>{isHi ? 'सक्रिय चर राशि:' : 'Active Chara Rashi:'}</span>
                <strong className="text-astro-text">{currentTriangulation.jaiminiEvidence.activeCharaDashaSign}</strong>
              </div>

              <div className="flex items-center justify-between text-astro-secondary-text">
                <span>{isHi ? 'आरूढ़ लग्न भाव:' : 'Arudha Lagna House:'}</span>
                <span className="font-bold text-astro-primary">H{currentTriangulation.jaiminiEvidence.arudhaLagnaHouse}</span>
              </div>

              <p className="text-astro-secondary-text text-[11px] leading-relaxed pt-1">
                {isHi ? currentTriangulation.jaiminiEvidence.explanationHi : currentTriangulation.jaiminiEvidence.explanationEn}
              </p>
            </div>
          </div>

          <div className="pt-2 border-t border-astro-border flex items-center justify-between text-xs">
            <span className="text-astro-secondary-text">{isHi ? 'धारा स्कोर:' : 'Stream Score:'}</span>
            <span className="font-extrabold text-astro-primary">{currentTriangulation.jaiminiEvidence.score}/100</span>
          </div>
        </div>
      </div>

      {/* Detailed Factors Breakdown (Learner & Pro View) */}
      {(isLearner || isPro) && (
        <div className="space-y-4 pt-2">
          {/* Supporting & Limiting Factors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            {/* Supporting Factors */}
            <div className="p-3.5 bg-white rounded-xl border border-emerald-200 space-y-2">
              <h5 className="font-bold text-emerald-900 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>{isHi ? 'समर्थक शास्त्रीय कारक' : 'Supporting Astrological Indicators'}</span>
              </h5>
              <ul className="space-y-1.5 text-astro-text">
                {(isHi ? currentTriangulation.supportingFactorsHi : currentTriangulation.supportingFactorsEn).map((factor, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-emerald-600 font-bold">•</span>
                    <span className="leading-snug">{factor}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Limiting Factors / Caution */}
            <div className="p-3.5 bg-white rounded-xl border border-amber-200 space-y-2">
              <h5 className="font-bold text-amber-900 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span>{isHi ? 'सीमित या विलंबकारी घटक' : 'Limiting & Delaying Factors'}</span>
              </h5>
              <ul className="space-y-1.5 text-astro-text">
                {(isHi ? currentTriangulation.limitingFactorsHi : currentTriangulation.limitingFactorsEn).map((factor, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold">•</span>
                    <span className="leading-snug">{factor}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contradictions Box if any */}
          {currentTriangulation.contradictionsEn && currentTriangulation.contradictionsEn.length > 0 && (
            <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl text-xs space-y-1">
              <h5 className="font-bold text-rose-900 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-rose-600" />
                <span>{isHi ? 'धारा-स्तरीय मतभेद एवं विरोधाभास' : 'Cross-Stream Contradictions & Caveats'}</span>
              </h5>
              <ul className="space-y-1 text-rose-900">
                {(isHi ? currentTriangulation.contradictionsHi : currentTriangulation.contradictionsEn).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-rose-600 font-bold">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Pro Mode: Evidence Trace Table */}
          {isPro && currentTriangulation.evidenceTrace && currentTriangulation.evidenceTrace.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between">
                <h5 className="text-xs font-bold text-astro-primary uppercase tracking-wider flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5 text-astro-accent" />
                  <span>{isHi ? 'गणितीय प्रमाण पदचिह्न (Evidence Trace)' : 'Mathematical Evidence Trace Log'}</span>
                </h5>
                {onOpenEvidenceDrawer && (
                  <button
                    onClick={() => onOpenEvidenceDrawer(activeType)}
                    className="text-xs text-astro-primary hover:text-astro-accent font-bold flex items-center gap-1 cursor-pointer transition-colors"
                  >
                    <BookOpen className="w-3.5 h-3.5" />
                    <span>{isHi ? 'शास्त्रीय सूत्र उद्धरण' : 'Classical Shastra Sutras'}</span>
                    <ArrowRight className="w-3 h-3" />
                  </button>
                )}
              </div>

              <div className="overflow-x-auto rounded-xl border border-astro-border">
                <table className="w-full text-left text-xs bg-white">
                  <thead className="bg-astro-card border-b border-astro-border text-astro-primary font-bold text-[11px]">
                    <tr>
                      <th className="p-2.5">{isHi ? 'पद्धति' : 'Stream'}</th>
                      <th className="p-2.5">{isHi ? 'शास्त्रीय संकेतक' : 'Indicator'}</th>
                      <th className="p-2.5">{isHi ? 'विश्लेषण निष्कर्ष' : 'Finding'}</th>
                      <th className="p-2.5 text-center">{isHi ? 'प्रभाव' : 'Impact'}</th>
                      <th className="p-2.5 text-right">{isHi ? 'भार' : 'Weight'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-astro-border">
                    {currentTriangulation.evidenceTrace.map((item, idx) => (
                      <tr key={idx} className="hover:bg-astro-bg">
                        <td className="p-2.5 font-bold text-astro-primary whitespace-nowrap">{item.stream}</td>
                        <td className="p-2.5 font-medium text-astro-text whitespace-nowrap">{item.indicator}</td>
                        <td className="p-2.5 text-astro-secondary-text">
                          {isHi && item.findingHi ? item.findingHi : item.finding}
                        </td>
                        <td className="p-2.5 text-center whitespace-nowrap">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              item.impact === 'POSITIVE'
                                ? 'bg-emerald-100 text-emerald-800'
                                : item.impact === 'NEGATIVE'
                                ? 'bg-rose-100 text-rose-800'
                                : 'bg-astro-secondary-surface text-astro-secondary-text'
                            }`}
                          >
                            {item.impact}
                          </span>
                        </td>
                        <td className="p-2.5 text-right font-mono font-bold text-astro-text whitespace-nowrap">
                          {Math.round(item.weight * 100)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
